import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import type { ArticleContent } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.click';

interface ArticleLayoutProps {
  article: ArticleContent;
  relatedArticles: { id: string; title: string }[];
}

export default function ArticleLayout({ article, relatedArticles }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleUrl = `${siteUrl}/${article.id}`;

  return (
    <>
      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
              { '@type': 'ListItem', position: 2, name: 'Articles', item: `${siteUrl}/what-is-litbuy-spreadsheet` },
              { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
            ],
          }),
        }}
      />

      <section className="pt-28 pb-10 md:pt-36 md:pb-14 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-700">Articles</span>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
          </div>

          <span className="inline-block text-xs font-label font-semibold bg-accent-100 text-accent-800 px-3 py-1.5 rounded-full mb-4">
            Guide
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
            {article.title}
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            {article.subtitle}
          </p>

          <div className="mt-6 flex items-center gap-3 text-xs text-foreground-500">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </span>
            <span>{article.readTime}</span>
            <span className="text-foreground-300">|</span>
            <span>Updated June 2026</span>
          </div>
        </div>
      </section>

      <section className="bg-background-50 pb-10">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <img
            src={article.image}
            alt={article.title}
            className="w-full rounded-2xl"
          />
        </div>
      </section>

      <section className="py-6 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              Table of Contents
            </h2>
            <ul className="space-y-2">
              {article.sections.map((section, idx) => (
                <li key={idx}>
                  <a
                    href={`#section-${idx}`}
                    className="text-sm text-foreground-600 hover:text-primary-600 transition-colors flex items-center gap-2"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                    {section.heading}
                  </a>
                </li>
              ))}
              {article.faqs && article.faqs.length > 0 && (
                <li>
                  <a
                    href="#section-faq"
                    className="text-sm text-foreground-600 hover:text-primary-600 transition-colors flex items-center gap-2"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                    Frequently Asked Questions
                  </a>
                </li>
              )}
              <li>
                <a
                  href="#section-conclusion"
                  className="text-sm text-foreground-600 hover:text-primary-600 transition-colors flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                  Conclusion
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <article className="py-8 md:py-12 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="space-y-12">
            {article.sections.map((section, idx) => (
              <section key={idx} id={`section-${idx}`}>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                  {section.heading}
                </h2>
                {section.content.map((paragraph, pIdx) => (
                  <p
                    key={pIdx}
                    className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}
                <p className="text-sm text-foreground-500 mt-2">
                  <Link to="/" className="text-primary-600 hover:text-primary-700 underline">← Back to LitBuy Spreadsheet Hub</Link>
                </p>
              </section>
            ))}

            {article.faqs && article.faqs.length > 0 && (
              <section id="section-faq">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {article.faqs.map((faq, idx) => (
                    <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/70">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <h3 className="text-sm font-label font-semibold text-foreground-800 pr-4">{faq.q}</h3>
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-45 transition-transform">
                          <i className="ri-add-line"></i>
                        </span>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            <section id="section-conclusion">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Conclusion
              </h2>
              {article.conclusion.map((paragraph, idx) => (
                <p
                  key={idx}
                  className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                >
                  {paragraph}
                </p>
              ))}
            </section>

            <div className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center">
              <h3 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-3">
                Ready to Start Shopping Smarter?
              </h3>
              <p className="text-sm text-background-50/80 max-w-md mx-auto mb-6">
                Visit Kaspreadsheet now and explore thousands of fashion items across all categories.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-7 py-3 bg-background-50 text-primary-600 rounded-full font-label font-semibold text-sm hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  litbuy spreadsheet
                  <span className="w-4 h-4 inline-flex items-center justify-center ml-2">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
                <Link
                  to="/"
                  className="px-7 py-3 border-2 border-background-50/70 text-background-50 rounded-full font-label font-semibold text-sm hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-8">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                Explore More Resources
              </h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-label font-medium bg-background-100 text-foreground-700 px-4 py-2 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-home-line"></i></span>
                  LitBuy Spreadsheet Hub
                </Link>
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1.5 text-xs font-label font-medium bg-background-100 text-foreground-700 px-4 py-2 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-shopping-bag-line"></i></span>
                  Shop Kaspreadsheet
                </a>
                {relatedArticles.slice(0, 3).map((ra) => (
                  <Link key={ra.id} to={`/${ra.id}`} className="inline-flex items-center gap-1.5 text-xs font-label font-medium bg-background-100 text-foreground-700 px-4 py-2 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-article-line"></i></span>
                    {ra.title.length > 30 ? ra.title.slice(0, 30) + '...' : ra.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>

      {article.faqs && article.faqs.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: article.faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: { '@type': 'Answer', text: faq.a },
              })),
            }),
          }}
        />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.subtitle,
            datePublished: '2026-06-20',
            dateModified: '2026-06-20',
            author: { '@type': 'Organization', name: 'LitBuy Spreadsheet Hub' },
            mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
          }),
        }}
      />
    </>
  );
}